import { useRef, useEffect, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';

const useDraggableItem = ({
  type,
  index,
  onMove,
  onDrop,
  item,
  id,
  endDrag,
  canDrag = true,
}) => {
  const ref = useRef(null);
  const isDraggingRef = useRef(false);
  const [, setForceUpdate] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!isDraggingRef.current) return;
      setForceUpdate((prev) => !prev);
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  const [{ handlerId }, drop] = useDrop({
    accept: type,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current || !monitor.isOver({ shallow: true })) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = (clientOffset).y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      onMove(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    drop(item, monitor) {
      onDrop(item, monitor);
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: type,
    canDrag: canDrag,
    item: () => ({ id, index, item }),
    collect: (monitor) => {
      const dragging = monitor.isDragging();
      isDraggingRef.current = dragging;
      return { isDragging: dragging };
    },
    end: (draggedItem, monitor) => {
      isDraggingRef.current = false;
      if (endDrag && typeof endDrag === 'function') {
        return endDrag(draggedItem, monitor);
      }
    },
  });

  return {
    ref,
    handlerId,
    isDragging,
    drag,
    drop,
  };
};

export default useDraggableItem;
